import type { AxiosProgressEvent } from "axios";
import type { HandledApiResponse } from "@entrilia/shared/api/axiosHelper";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import adminApi from "../../../api/adminApi";
import blobStorageApi from "../../../api/blobStorageApi";
import type { VideoLibraryItem } from "../../../api/types/videoLibraryTypes";

const getUploadUrlForVideo = withErrorHandling(adminApi.getUploadUrlForVideo);
const completeVideoUpload = withErrorHandling(adminApi.completeVideoUpload);
const uploadFileToBlobStorage = withErrorHandling(blobStorageApi.uploadFileToBlobStorage);

export interface FileUploadParams {
  file: File;
  duration: number;
  title: string;
  description?: string;
  categoryIds: string[];
  uploadedAt?: string;
}

const uploadVideoFileToLibrary = async (
  params: FileUploadParams,
  onUploadProgress?: (e: AxiosProgressEvent) => void
): Promise<HandledApiResponse<VideoLibraryItem>> => {
  const [uploadUrlResp, uploadUrlError] = await getUploadUrlForVideo({
    title: params.title,
    fileName: params.file.name,
    descriptionUserInput: params.description,
  });

  if (uploadUrlError) {
    return [undefined, uploadUrlError];
  }

  const { uploadUrl, dataCatalogId } = uploadUrlResp;

  const [, uploadError] = await uploadFileToBlobStorage(uploadUrl, params.file, onUploadProgress);
  if (uploadError) {
    return [undefined, uploadError];
  }

  const [videoLibraryItem, completeError] = await completeVideoUpload({
    title: params.title,
    descriptionUserInput: params.description,
    dataCatalogId,
    categoryIds: params.categoryIds,
    duration: params.duration,
    uploadedAt: params.uploadedAt,
  });

  if (completeError) {
    return [undefined, completeError];
  }

  return [videoLibraryItem, undefined];
};

const fileUploaderService = {
  uploadVideoFileToLibrary,
};

export default fileUploaderService;
